import React, { useEffect } from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import AppBarComponent from './components/AppBar/AppBarComponent';
import Home from './screens/home/Home';
import ResultPage from './screens/results_page/ResultPage';
import LifeResultPage from './screens/results_page/LifeResultPage';
import FuneralResultPage from './screens/results_page/FuneralResultPage';
import MedicalGapResultPage from './screens/results_page/MedicalGapResultPage';
import MedicalAidResultPage from './screens/results_page/MedicalAidResultPage';
import MedicalInsuranceResultPage from './screens/results_page/MedicalInsuranceResultPage';
import PetInsuranceResultPage from './screens/results_page/PetResultPage';
import TravelInsuranceResultPage from './screens/results_page/TravelResultPage';
import ScratchAndDentInsuranceResultPage from './screens/results_page/ScratchAndDentResultPage';
import TyreAndRimResultPage from './screens/results_page/TyreAndRimResultPage';
import MotorWarrantyResultPage from './screens/results_page/MotorWarrantyResultPage';
import VehicleTrackingResultPage from './screens/results_page/VehicleTrackingResultPage';
import DebtCounsellingResultPage from './screens/results_page/DebtCounsellingResultPage';
import BusinessInsuranceResultPage from './screens/results_page/BusinessInsuranceResultPage';
import BusinessLegalCoverResultPage from './screens/results_page/BusinessLegalCoverResultPage';
import CommercialPropertyResultPage from './screens/results_page/CommercialPropertyResultPage';
import ProfessionalLiabilityResultPage from './screens/results_page/ProfessionalLiabilityResultPage';
import BuildingInsuranceResultPage from './screens/results_page/BuildingInsuranceResultPage';
import PersonalLoanResultPage from './screens/results_page/PersonalLoanResultPage';
import DebtConsolidationResultPage from './screens/results_page/DebtConsolidationResultPage';
import QuestionEngine from './screens/question_engine/QuestionEngine';
import Accounts from './screens/account/Accounts';
import QuoteSelected from './screens/quote_selected/QuoteSelected';
import StandaloneCallback from './screens/quote_selected/StandaloneCallback';
import PersonalLoansThankYou from './screens/quote_selected/PersonalLoansThankYou';
import FlexicareThankYou from './screens/quote_selected/FlexicareThankYou';
import ExternalQuoteAccept from './screens/quote_selected/ExternalQuoteAccept';
import BlogPost from './screens/blog/BlogPost';
import BlogList from './screens/blog/BlogList';
import SpinOff from './screens/spin-off/SpinOff';
import { MobileProvider } from './contexts/MobileContext';
import { setupGlobalShortcuts } from './utils/globalShortcuts';
import HomeContentsResultPage from './screens/results_page/HomeContentsResultPage';
import ServicePlansResultPage from './screens/results_page/ServicePlansResultPage';
import MotorcycleInsuranceResultPage from './screens/results_page/MotorcycleInsuranceResultPage';
import FibreResultPage from './screens/results_page/FibreResultPage';
import CombinedInsuranceResultPage from './screens/results_page/CombinedInsuranceResultPage';
import NotFound from './screens/404/NotFound';
import Gone from './screens/410/Error';
import DocumentPage from './screens/document/DocumentPage';
import About from './screens/about/About';
import ContactUs from './screens/contact-us/ContactUs';
import {
  isValidatedWithin,
  ENABLE_OTP_SMS,
} from './utils/questionEngineOtpHelpers';
import { FLOW_SLUG_CONFIG } from './utils/flowSlugConfig';
import { toGtmProduct } from './utils/dataLayer';
import { getBaseProductRoute } from './utils/productNavigation';
import SessionManager from './utils/sessionManager';

const homePaths = [
  '/',
  '/car-insurance-quote/',
  '/funeral-insurance-quote/',
  '/life-insurance-quote/',
  '/medical-aid-quote/',
  '/pet-insurance-quote/',
  '/motor-warranty/',
  '/dents-and-scratches-quote/',
  '/travel-insurance/',
  '/medical-gap-cover-quote/',
  '/medical-insurance-quotes/',
  '/buildings-insurance-quote/',
  '/business-insurance-quote/',
  '/service-plan-quotes/',
  '/vehicle-tracking-quote/',
  '/tyre-and-rim-guard/',
  '/compare-fibre-deals/',
  '/motorcycle-insurance-quote/',
  '/home-insurance-quote/',
  '/personal-loans/',
  '/debt-counselling/',
  '/debt-consolidation-loans/',
  '/business-insurance-quote/car-and-commercial-property-insurance/',
  '/employee-benefits-quote/',
  '/business-insurance-quote/business-legal-cover/',
  '/business-insurance-quote/professional-liability-insurance-cover/',
  '/business-insurance-quote/event-liability-insurance/',
  '/online-insurance-quote/',
  '/insurance/',
  '/motor/',
  '/health/',
  '/money/',
  '/internet/',
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function VdnCapture() {
  const { search } = useLocation();
  useEffect(() => {
    SessionManager.captureVdnFromQueryString(search);
  }, [search]);
  return null;
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add('animations-enabled');
    return () => {
      document.documentElement.classList.remove('animations-enabled');
    };
  }, []);

  return (
    <MobileProvider>
      <Router>
        <ScrollToTop />
        <VdnCapture />
        <GTMPageTracker />
        <GlobalShortcutsWrapper />
        <ConditionalAppBar />
        <Routes>
          {homePaths.map((path) => (
            <Route key={path} path={path} element={<ProductLanding />} />
          ))}

          <Route path="/more/:slug" element={<DocumentPage />} />
          <Route
            path="/check-fibre-in-your-area"
            element={<Navigate to="/check-fibre-in-your-area/step1" replace />}
          />
          <Route
            path="/compare-fibre-deals/:step/*"
            element={<FibreLegacyStepRedirect />}
          />
          <Route
            path="/business-insurance-quote/:flowSlug/:step/*"
            element={<FlowRouteResolver prefix="business-insurance-quote" />}
          />
          <Route path="/:flowSlug/:step/*" element={<FlowRouteResolver />} />
          <Route path="/:spin-off-slug" element={<SpinOff />} />
          <Route path="/vehicleQuoteResults" element={<ResultPage />} />
          <Route path="/lifeQuoteResults" element={<LifeResultPage />} />
          <Route
            path="/medicalGapQuoteResults"
            element={<MedicalGapResultPage />}
          />
          <Route
            path="/medicalAidQuoteResults"
            element={<MedicalAidResultPage />}
          />
          <Route
            path="/medicalInsuranceQuoteResults"
            element={<MedicalInsuranceResultPage />}
          />
          <Route path="/funeralQuoteResults" element={<FuneralResultPage />} />
          <Route path="/petQuoteResults" element={<PetInsuranceResultPage />} />
          <Route
            path="/travelQuoteResults"
            element={<TravelInsuranceResultPage />}
          />
          <Route
            path="/scratchAndDentQuoteResults"
            element={<ScratchAndDentInsuranceResultPage />}
          />
          <Route
            path="/tyreAndRimQuoteResults"
            element={<TyreAndRimResultPage />}
          />
          <Route
            path="/servicePlansQuoteResults"
            element={<ServicePlansResultPage />}
          />
          <Route
            path="/motorWarrantyQuoteResults"
            element={<MotorWarrantyResultPage />}
          />
          <Route
            path="/vehicleTrackingQuoteResults"
            element={<VehicleTrackingResultPage />}
          />
          <Route
            path="/debtCounsellingQuoteResults"
            element={<DebtCounsellingResultPage />}
          />
          <Route
            path="/businessInsuranceQuoteResults"
            element={<BusinessInsuranceResultPage />}
          />
          <Route
            path="/employeeBenefitsCallbackRequest"
            element={<StandaloneCallback />}
          />
          <Route
            path="/businessLegalCoverQuoteResults"
            element={<BusinessLegalCoverResultPage />}
          />
          <Route
            path="/commercialPropertyQuoteResults"
            element={<CommercialPropertyResultPage />}
          />
          <Route
            path="/professionalLiabilityQuoteResults"
            element={<ProfessionalLiabilityResultPage />}
          />
          <Route
            path="/homeContentsQuoteResults"
            element={<HomeContentsResultPage />}
          />
          <Route
            path="/buildingInsuranceQuoteResults"
            element={<BuildingInsuranceResultPage />}
          />
          <Route
            path="/motorcycleQuoteResults"
            element={<MotorcycleInsuranceResultPage />}
          />
          <Route
            path="/combinedInsuranceQuoteResults"
            element={<CombinedInsuranceResultPage />}
          />
          <Route
            path="/personalLoanQuoteResults"
            element={<PersonalLoanResultPage />}
          />
          <Route
            path="/debtConsolidationQuoteResults"
            element={<DebtConsolidationResultPage />}
          />
          <Route path="/fibreQuoteResults" element={<FibreResultPage />} />
          <Route path="/accounts" element={<AccountsRoute />} />
          <Route path="/quoteSelection" element={<QuoteSelected />} />
          <Route
            path="/personal-loans/thankyou"
            element={<PersonalLoansThankYou />}
          />
          <Route path="/flexicare-thank-you" element={<FlexicareThankYou />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/page/:page" element={<BlogList />} />
          <Route path="/blog/:category/" element={<BlogList />} />
          <Route path="/blog/:category/page/:page" element={<BlogList />} />
          <Route path="/blog/:category/:slug" element={<BlogPost />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/410-gone" element={<Gone />} />

          {/* 404 Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </MobileProvider>
  );
}

// Product landing pages double as external quote acceptance entry points. When a
// shared quote link lands here with the quote/person keys in the query string,
// accept the quote and forward to the thank you page; otherwise render the page.
function ProductLanding() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const quoteId = params.get('quoteId');
  const personId = params.get('personId');

  if (quoteId && personId) {
    return <ExternalQuoteAccept quoteId={quoteId} personId={personId} />;
  }

  return <Home />;
}

function ConditionalAppBar() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/more/')) return null;
  return <AppBarComponent loggedIn={false} />;
}

function GTMPageTracker() {
  const location = useLocation();

  useEffect(() => {
    const flowSlug = getBaseProductRoute(location.pathname).replace(/^\//, '');
    const flowConfig = FLOW_SLUG_CONFIG[flowSlug];
    const product = flowConfig ? toGtmProduct(flowConfig.product) : flowSlug;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: location.pathname + location.search,
      ...(product && { product }),
    });
  }, [location]);

  return null;
}

function GlobalShortcutsWrapper() {
  const navigate = useNavigate();

  useEffect(() => {
    const cleanup = setupGlobalShortcuts(navigate);
    return cleanup;
  }, [navigate]);

  return null;
}

// Old Fibre quote-flow URLs (/compare-fibre-deals/stepN/...) now live under
// /check-fibre-in-your-area/stepN/... — preserve the step + any trailing
// splat/query so in-progress links and indexed pages land on the right step.
function FibreLegacyStepRedirect() {
  const location = useLocation();
  const target =
    location.pathname.replace(
      '/compare-fibre-deals',
      '/check-fibre-in-your-area'
    ) + location.search;

  return <Navigate to={target} replace />;
}

function FlowRouteResolver({ prefix }) {
  const params = useParams();
  const step = params.step || '';
  const splat = params['*'] || '';
  const isQuestionStep = /^step-?\d+$/.test(step) && splat.length === 0;

  return isQuestionStep ? (
    <QuestionEngine flowSlugPrefix={prefix} />
  ) : (
    <SpinOff />
  );
}

function AccountsRoute() {
  const location = useLocation();

  if (!ENABLE_OTP_SMS || !isValidatedWithin()) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Accounts />;
}

export default App;
